// Via de import map (snippets/scripts.liquid) en dus met de `?v=`-hash van asset_url.
// Een relatieve import gaat naar het CDN-pad zonder versie, en dat wordt een jaar
// gecachet: na de release van SHO-9535 leverde het CDN de helper van vóór die release,
// die `buildCartEstimateLines` niet exporteert. Deze module viel daardoor stil in de
// winkelmand en de drawer — de reden dat dit bestand ooit `-v2` in de naam kreeg.
import {
  buildCartEstimateLines,
  buildEstimateUrls,
} from '@theme/cart-hds-premium-points-core-v2';
import { getCart } from '@theme/cart-state';

/** Berekent HDS Premium-punten over de volledige winkelmand. */
(function () {
  if (window.__hdsCartPremiumPointsInit) return;
  window.__hdsCartPremiumPointsInit = true;

  var SEL = '[data-hds-cart-premium-root]';
  var DEBOUNCE_MS = 250;
  var debounceTimer = null;
  // Het endpoint doet er ~0,75 s over. Binnen één paginabezoek is het antwoord per
  // URL constant, dus onthouden we het: de drawer opnieuw openen kost dan niets meer.
  var cache = new Map();
  // Volgnummer van de laatst gestarte ronde; alleen die mag het getal schrijven.
  var laatsteRun = 0;

  function formatPoints(sum) {
    var suffix = sum === 1 ? ' punt' : ' punten';
    return '+' + sum.toLocaleString('nl-NL') + suffix;
  }

  function setAllValues(text) {
    document.querySelectorAll(SEL).forEach(function (root) {
      var el = root.querySelector('[data-hds-cart-premium-value]');
      if (el) el.textContent = text;
    });
  }

  /** Call die de winkelmand-pagina al startte tijdens het parsen; anders zelf beginnen. */
  function haalOp(url) {
    var vooraf = window.hdsPremiumPointsPrefetch && window.hdsPremiumPointsPrefetch[url];
    if (vooraf) return vooraf;
    return fetch(url, {
      method: 'GET',
      headers: { Accept: 'application/json' },
      credentials: 'same-origin',
    }).then(function (res) {
      return res.ok ? res.json() : null;
    });
  }

  function pointsForUrl(url) {
    // De belofte zelf in de cache, niet pas het antwoord: een tweede trigger binnen die
    // ~0,75 s (de drawer stuurt er meteen na het laden een) lift zo mee op de eerste call.
    if (cache.has(url)) return cache.get(url);

    var belofte = haalOp(url)
      .then(function (data) {
        // Let op: Number(null) is 0. Zonder deze check telt een mislukte regel als
        // 0 punten en toont de winkelmand "+0 punten" i.p.v. "onbekend".
        if (!data) return null;
        var points = Number(data.points);
        return Number.isFinite(points) ? points : null;
      })
      .catch(function () {
        return null;
      })
      .then(function (points) {
        // Mislukte regels niet vasthouden: een volgende poging mag het opnieuw proberen.
        if (points === null) cache.delete(url);
        return points;
      });

    cache.set(url, belofte);
    return belofte;
  }

  function fetchCartPoints(lines) {
    return Promise.all(buildEstimateUrls(lines).map(pointsForUrl)).then(function (results) {
      // Eén onbekende regel maakt het totaal onbetrouwbaar; liever niets tonen dan te weinig.
      var onbekend = results.some(function (points) {
        return points === null;
      });
      return results.length && !onbekend
        ? results.reduce(function (sum, points) { return sum + points; }, 0)
        : null;
    });
  }

  /** Regels die Liquid al meegaf, zodat de eerste weergave geen /cart.js hoeft op te halen. */
  function linesFromDom() {
    var root = document.querySelector(SEL + '[data-lines]');
    if (!root) return null;
    try {
      var lines = JSON.parse(root.dataset.lines);
      return Array.isArray(lines) ? lines : null;
    } catch (e) {
      return null;
    }
  }

  function render(lines, run) {
    if (run !== laatsteRun) return;
    if (!lines.length) {
      setAllValues('—');
      return;
    }
    return fetchCartPoints(lines).then(function (points) {
      // Een nieuwere ronde is inmiddels begonnen: die kent de actuele mand, deze niet meer.
      // Zonder deze check kan een trage ronde een verse uitkomst overschrijven — bijvoorbeeld
      // wanneer het aantal omhoog en meteen weer omlaag gaat en de tweede ronde uit de cache komt.
      if (run !== laatsteRun) return;
      setAllValues(points === null ? '—' : formatPoints(points));
    });
  }

  function updateRoots(opties) {
    if (!document.querySelector(SEL)) return;

    var run = ++laatsteRun;
    var cartFromEvent = opties && opties.cart;
    // De regels uit Liquid gelden alleen bij de eerste weergave: na een wijziging kan de
    // markup nog de oude zijn. Dan dus verse data — uit het event als dat de cart
    // meestuurt, anders /cart.js.
    var lines = cartFromEvent
      ? buildCartEstimateLines(cartFromEvent)
      : opties && opties.eersteRender
        ? linesFromDom()
        : null;
    if (lines) return render(lines, run);

    return getCart()
      .then(function (cart) {
        return render(buildCartEstimateLines(cart), run);
      })
      .catch(function () {
        if (run === laatsteRun) setAllValues('—');
      });
  }

  function schedule(cartFromEvent) {
    if (debounceTimer) clearTimeout(debounceTimer);
    debounceTimer = setTimeout(function () {
      debounceTimer = null;
      updateRoots({ cart: cartFromEvent });
    }, DEBOUNCE_MS);
  }

  window.hdsRefreshCartPremiumPoints = function () {
    schedule();
  };

  // Eerste weergave niet uitstellen: debouncen is er voor de reeks events die op
  // één cart-wijziging volgt, en die is er bij het laden van de pagina nog niet.
  function eersteRender() {
    updateRoots({ eersteRender: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', eersteRender);
  } else {
    eersteRender();
  }

  document.addEventListener('cart:update', function (event) {
    var cart = event && event.detail && event.detail.resource;
    schedule(cart && Array.isArray(cart.items) ? cart : null);
  });
})();
